import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send } from 'lucide-react';
import { trackEvent, trackLeadGenerated } from '../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'fma_chat_v1';
const GREETING = "Hi, I'm Mia from FindMeAgent. Thinking of selling, or curious what your home is worth? Ask me anything, or tell me the suburb and I'll see which local agents have sold nearby.";
const QUICK_REPLIES = ["I'm thinking of selling", "What's my home worth?", 'What do agents charge?', 'How does this work?'];
const HIDDEN_PREFIXES = ['/admin', '/thank-you'];

const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

const loadState = () => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    if (saved?.sessionId) return saved;
  } catch (_) { /* ignore */ }
  return { sessionId: newId(), messages: [{ role: 'assistant', content: GREETING }], converted: false };
};

const ChatWidget = () => {
  const location = useLocation();
  const [state, setState] = useState(loadState);
  const [open, setOpen] = useState(false);
  const [teaser, setTeaser] = useState(false);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const hidden = HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  useEffect(() => {
    if (hidden || open || sessionStorage.getItem('fma_chat_teaser_seen')) return undefined;
    const t = setTimeout(() => setTeaser(true), 8000);
    return () => clearTimeout(t);
  }, [hidden, open]);

  useEffect(() => {
    if (open && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [state.messages, open, streaming]);

  const openChat = () => {
    setOpen(true);
    setTeaser(false);
    sessionStorage.setItem('fma_chat_teaser_seen', '1');
    trackEvent('chat_open', { page: location.pathname });
    setTimeout(() => inputRef.current?.focus(), 150);
  };

  const dismissTeaser = (e) => {
    e.stopPropagation();
    setTeaser(false);
    sessionStorage.setItem('fma_chat_teaser_seen', '1');
  };

  const send = useCallback(async (text) => {
    const content = text.trim();
    if (!content || streaming) return;
    setInput('');
    setStreaming(true);
    setState((s) => ({ ...s, messages: [...s.messages, { role: 'user', content }, { role: 'assistant', content: '' }] }));
    if (state.messages.length === 1) trackEvent('chat_first_message', { page: location.pathname });

    const appendDelta = (delta) => setState((s) => {
      const msgs = s.messages.slice();
      const last = msgs[msgs.length - 1];
      msgs[msgs.length - 1] = { ...last, content: last.content + delta };
      return { ...s, messages: msgs };
    });

    try {
      const res = await fetch(`${API}/api/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: state.sessionId, message: content, page: location.pathname }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop();
        for (const part of parts) {
          const line = part.split('\n').find((l) => l.startsWith('data: '));
          if (!line) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.type === 'delta') appendDelta(ev.content);
          else if (ev.type === 'done' && ev.lead_submitted) {
            trackLeadGenerated('chatbot');
            setState((s) => ({ ...s, converted: true }));
          }
        }
      }
    } catch (err) {
      appendDelta("Sorry, I lost connection for a moment. Please try again, or use the form on this page and we'll be in touch within one business day.");
    } finally {
      setStreaming(false);
    }
  }, [state.sessionId, state.messages.length, streaming, location.pathname]);

  if (hidden) return null;

  const showQuickReplies = state.messages.length === 1 && !streaming;

  return (
    <div className="chat-root" data-testid="chat-widget">
      {!open && (
        <div className="chat-launcher-wrap">
          {teaser && (
            <button type="button" className="chat-teaser" onClick={openChat} data-testid="chat-teaser">
              <span className="chat-teaser-text">Thinking of selling? Find out what your home could be worth, free.</span>
              <span className="chat-teaser-close" role="button" aria-label="Dismiss" onClick={dismissTeaser} data-testid="chat-teaser-close"><X size={14} /></span>
            </button>
          )}
          <button type="button" className="chat-launcher" onClick={openChat} aria-label="Chat with Mia" data-testid="chat-launcher">
            <MessageCircle size={26} />
            <span className="chat-launcher-dot" />
          </button>
        </div>
      )}

      {open && (
        <div className="chat-panel" role="dialog" aria-label="Chat with Mia from FindMeAgent" data-testid="chat-panel">
          <div className="chat-header">
            <div className="chat-avatar">FM</div>
            <div className="chat-header-text">
              <div className="chat-header-name">Mia · FindMeAgent</div>
              <div className="chat-header-status"><span className="chat-status-dot" /> Typically replies instantly</div>
            </div>
            <button type="button" className="chat-close" onClick={() => setOpen(false)} aria-label="Close chat" data-testid="chat-close"><X size={20} /></button>
          </div>

          <div className="chat-messages" ref={listRef} data-testid="chat-messages">
            {state.messages.map((m, i) => (
              <div key={i} className={`chat-msg chat-msg-${m.role}`} data-testid={`chat-msg-${m.role}-${i}`}>
                {m.content || (streaming && i === state.messages.length - 1 ? <span className="chat-typing"><i /><i /><i /></span> : '')}
              </div>
            ))}
            {state.converted && (
              <div className="chat-success" data-testid="chat-lead-success">Details received. A FindMeAgent team member will be in touch within one business day.</div>
            )}
          </div>

          {showQuickReplies && (
            <div className="chat-quick" data-testid="chat-quick-replies">
              {QUICK_REPLIES.map((q) => (
                <button key={q} type="button" className="chat-chip" onClick={() => send(q)} data-testid={`chat-chip-${q.replace(/[^a-z]+/gi, '-').toLowerCase()}`}>{q}</button>
              ))}
            </div>
          )}

          <form className="chat-input-row" onSubmit={(e) => { e.preventDefault(); send(input); }}>
            <input
              ref={inputRef}
              className="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message…"
              maxLength={1000}
              disabled={streaming}
              data-testid="chat-input"
            />
            <button type="submit" className="chat-send" disabled={streaming || !input.trim()} aria-label="Send" data-testid="chat-send"><Send size={18} /></button>
          </form>
          <div className="chat-footnote">Free service · Your details are only shared with your matched agents</div>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
